// Small popup panels and menus.

import { ChevronDown, ChevronUp, Information, InformationFilled } from '@carbon/icons-react';
import { PopupBase } from 'platform-specific/popup';
import { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { TLabel } from 'util/stdtypes';
import { undefA, undefS, undefTL } from 'util/stdtypes';

import { HorizBox, HoverView, Pad } from './basics';
import { TextButton } from './button';
import { colorSurfacePrimary, colorSurfaceToastDark, colorTextOnToastDark, colorTextPrimary, colorTextSecondary } from './color';
import { UtilityText } from './text';

export function PopupPanel({ children, popupContent, alignRight = false, testID = undefS, setHover = undefA }) {
    const s = PopupPanelStyle;
    const [shown, setShown] = useState(false);
    return (
        <View style={{ alignSelf: 'flex-start' }}>
            <PopupBase alignRight={alignRight} popupContent={popupContent} setHover={setHover} setShown={setShown} testID={testID}>
                <View style={s.button}>
                    {children}
                    <Pad size={8} />
                    {shown ? <ChevronUp color={colorTextPrimary} size={16} /> : <ChevronDown color={colorTextPrimary} size={16} />}
                </View>
            </PopupBase>
        </View>
    );
}

const PopupPanelStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        alignItems: 'center',
    },
});

// Need to have this here rather than in platform-specific, because it's hard for
// other components to import from platform-specific
export function Popup(params) {
    return <PopupBase {...params} />;
}

export function DropDownSelector({
    testID = undefS,
    label = undefTL,
    text = undefS,
    options = undefA,
    value = undefS,
    onChange = (k: string) => {},
}) {
    const [hover, setHover] = useState(false);
    const selectedOption = options.find((o) => o.key == value) || options[0];
    function popupContent({ onClose }) {
        return (
            <View style={{ backgroundColor: colorSurfacePrimary }}>
                {options.map((o, i) => (
                    <View key={i}>
                        {i != 0 && <Pad size={20} />}
                        <TextButton
                            label={o.label}
                            onPress={() => {
                                onChange(o.key);
                                onClose();
                            }}
                            text={o.text}
                            type='small'
                        />
                    </View>
                ))}
            </View>
        );
    }
    return (
        <PopupPanel alignRight popupContent={popupContent} setHover={setHover} testID={testID}>
            <HorizBox>
                <UtilityText label={label} text={text} type='small' weight='medium' />
                {
                    (label || text) && (
                        <UtilityText text=': ' type='small' weight='medium' />
                    ) /** Do not render a lone colon if no label provided*/
                }
                <UtilityText label={selectedOption.label} type='small' underline={hover} weight='medium' />
            </HorizBox>
        </PopupPanel>
    );
}

type PopupTipProps = {
    label?: TLabel;
    text?: string;
};

export function PopupTip({ label, text }: PopupTipProps) {
    const s = PopupTipStyle;
    const [shown, setShown] = useState(false);
    const [hover, setHover] = useState(false);

    const popupContent = () => {
        return (
            <View style={s.contentContainer}>
                <UtilityText color={colorTextOnToastDark} label={label} text={text} />
            </View>
        );
    };

    return (
        <Popup alignTop popupContent={popupContent} popupStyle={s.popup} setHover={setHover} setShown={setShown} testID='popup-tip'>
            {hover || shown ? <InformationFilled color={colorTextPrimary} /> : <Information color={colorTextSecondary} />}
        </Popup>
    );
}

const PopupTipStyle = StyleSheet.create({
    popup: {
        backgroundColor: colorSurfaceToastDark,
        borderRadius: 8,
        padding: 20,
    },
    contentContainer: {
        maxWidth: 260,
    },
});
